// The /features hub (pages/features/index.vue, components/FeaturesList.vue): every feature card,
// grouped by the job it does, each group with one looping product scene.
//
// Rules:
// - A card links to its best existing page (feature, integration, solution, tutorial or release
//   note). No new routes: a feature without its own page links to the page that covers it.
// - Plan cues are never written by hand. `plans` names rows of data/pricing-features.json and
//   `planCue()` turns each row into "Every plan" or "<first plan> and up". The first row is the
//   card's own gate; any later row carries a `label` for the part that starts higher. A card with
//   no matching row (iPhone app, multi-currency) shows no cue rather than a guessed one.
// - Copy only recombines capabilities in pricing-features.json, data/release-notes.json or the
//   linked page. No payment fee, storage sizes, "unlimited users", "estimates" or "quotes" as a
//   product noun, or "tokens" (AGENTS.md, Product claims).
// - `scene` is a key of data/feature-motion.js (app strings are checked there), or "ai-chat" for
//   the AI AutoPilot chat (components/story/StoryChatStage.vue). A group without a scene yet
//   (Members) shows its intro and cards only.
// - /roadmap (the product map) renders these same groups and cards, so a new capability belongs here.
import pricing from "./pricing-features.json";

export interface HubPlanRow {
  key: string;
  label?: string;
  // Cue the first plan with the full feature, not a limited one ("Email only" on Cadet).
  full?: boolean;
}

export interface HubCard {
  name: string;
  copy: string;
  link: string;
  icon: string;
  plans: HubPlanRow[];
}

export interface HubGroup {
  id: string;
  eyebrow: string;
  title: string;
  intro: string;
  scene?: string;
  cards: HubCard[];
}

export const hubGroups: HubGroup[] = [
  {
    id: "win-work",
    eyebrow: "Win work",
    title: "From first form to signed proposal",
    intro: "Capture the request, follow up on time and send a proposal the client can accept on their own page.",
    scene: "features/quoting",
    cards: [
      {
        name: "Proposals",
        copy: "Line items, optional add-ons and a deposit on acceptance. Clients accept on their own page and you convert it to an invoice.",
        link: "/features/quoting",
        icon: "description",
        plans: [{ key: "proposals" }, { key: "proposalDesign", label: "Cover images" }],
      },
      {
        name: "Contracts & e-signature",
        copy: "Send contracts for signature in order, with an audit trail and a certificate of completion.",
        link: "/features/contracts",
        icon: "edit",
        plans: [{ key: "contracts" }],
      },
      {
        name: "Requests & intake forms",
        copy: "Drag-and-drop forms you can embed on any website, with auto-replies and request tracking until the request becomes a client.",
        link: "/solutions/client-intake-form-software",
        icon: "inbox",
        plans: [{ key: "intakeForms" }],
      },
      {
        name: "CRM & follow-ups",
        copy: "Client records, companies, custom fields and stages, with a follow-up queue that says who to contact today and why.",
        link: "/solutions/workspace-crm",
        icon: "people",
        plans: [{ key: "crm" }, { key: "leadSources", label: "Lead source reports" }],
      },
      {
        name: "Online booking",
        copy: "Booking pages where clients pick a free time and a team member, answer your questions and pay a deposit, with buffers and round-robin scheduling.",
        link: "/features/online-booking",
        icon: "calendar",
        plans: [{ key: "onlineBooking" }],
      },
      {
        name: "Funnels",
        copy: "Pages built from blocks and hosted for you, capturing leads straight into the CRM, with follow-up flows and funnel analytics.",
        link: "/solutions/funnel-builder-with-crm",
        icon: "funnel",
        plans: [{ key: "funnelBuilder" }, { key: "funnelPro", label: "Pro tools" }],
      },
    ],
  },
  {
    id: "do-the-work",
    eyebrow: "Do the work",
    title: "Projects, calendar and time in one place",
    intro: "Plan the work, book the time and log every billable hour against the client it belongs to.",
    scene: "features/schedule",
    cards: [
      {
        name: "Projects & tasks",
        copy: "Projects with stages, Kanban boards, task lists and subtasks, plus a timeline view with task dependencies.",
        link: "/tutorials/projects",
        icon: "accountTree",
        plans: [{ key: "projects" }, { key: "timelineView", label: "Timeline & dependencies" }],
      },
      {
        name: "Calendar & scheduling",
        copy: "A team calendar with recurring events, guest invites, Google Calendar and Meet links, and reminders before appointments.",
        link: "/features/schedule",
        icon: "calendar",
        plans: [{ key: "teamCalendar" }],
      },
      {
        name: "Time tracking",
        copy: "Timers, timesheets and billable rates, with tracked time billed straight onto an invoice.",
        link: "/features/time-tracking",
        icon: "schedule",
        plans: [{ key: "timeTracking" }],
      },
      {
        name: "Files",
        copy: "Client and project files with versions and share links, kept on the record they belong to.",
        link: "/features/files",
        icon: "folder",
        plans: [{ key: "fileManagement" }, { key: "folderPermissions", label: "Folder permissions" }],
      },
      {
        name: "Notes & reminders",
        copy: "Notes that work like documents, reminders, habits and focus tools, next to the work they support.",
        link: "/tutorials/reminders",
        icon: "bellAlert",
        plans: [{ key: "notesCalendar" }],
      },
      {
        name: "iPhone & iPad app",
        copy: "The same workspace as the web app, on iPhone and iPad.",
        link: "/features/mobile-app",
        icon: "phone",
        plans: [],
      },
    ],
  },
  {
    id: "get-paid",
    eyebrow: "Get paid",
    title: "Invoices that get paid, on the spot or on schedule",
    intro: "Clients pay online through your own Stripe account, and every payment lands on the invoice and the client record.",
    scene: "features/invoices",
    cards: [
      {
        name: "Invoices",
        copy: "Invoices from proposals, tracked time or the catalog, with a live preview and a page your client pays from.",
        link: "/features/invoices",
        icon: "receipt",
        plans: [{ key: "invoices" }, { key: "invoiceTemplates", label: "Templates" }],
      },
      {
        name: "Online payments",
        copy: "Cards, Apple Pay, Google Pay and ACH bank transfers, with deposits, split payments and saved payment methods.",
        link: "/features/payments",
        icon: "creditCard",
        plans: [{ key: "onlinePayments" }, { key: "paymentPlans", label: "Payment plans" }],
      },
      {
        name: "Flash Pay",
        copy: "Type an amount, then show a QR code or send a pay link. Each payment is booked as a paid invoice, with optional tips.",
        link: "/features/flash-pay",
        icon: "qrCode",
        plans: [{ key: "paymentLinks" }],
      },
      {
        name: "Retainers & recurring billing",
        copy: "Retainer balances that track every hour, recurring invoices and card autopay once the client agrees.",
        link: "/features/retainers-and-recurring-billing",
        icon: "renewCycle",
        plans: [{ key: "retainers" }],
      },
      {
        name: "Late-payment reminders",
        copy: "A gentle note, a firmer follow-up and a final notice that stop once the client pays, sent in your brand.",
        link: "/solutions/payment-reminder-software",
        icon: "bellAlert",
        plans: [{ key: "dunning" }, { key: "lateFees", label: "Automatic late fees" }],
      },
      {
        name: "Change orders",
        copy: "Versioned scope changes with the money impact shown, approved by the client from a link.",
        link: "/features/change-orders",
        icon: "edit",
        plans: [{ key: "changeOrders" }],
      },
      {
        name: "Multi-currency invoicing",
        copy: "Invoice in your client's currency, with tax summaries and documents in your client's language.",
        link: "/features/multi-currency-invoicing",
        icon: "language",
        plans: [],
      },
      {
        name: "PDF customization",
        copy: "Shape the proposals, invoices and documents your clients download, in your brand.",
        link: "/features/pdf-customization",
        icon: "description",
        plans: [{ key: "pdfCustomization" }],
      },
    ],
  },
  {
    id: "talk-to-clients",
    eyebrow: "Talk to clients",
    title: "Every email, text and call in one conversation",
    intro: "Each client's messages land in one thread, and everything they see from you carries your brand.",
    scene: "features/communications",
    cards: [
      {
        name: "Inbox & email",
        copy: "Your email in the workspace, matched to clients, with templates, signatures, scheduled sends and tasks from messages.",
        link: "/features/communications",
        icon: "email",
        plans: [{ key: "emailInbox" }, { key: "sharedInbox", label: "Shared inbox" }],
      },
      {
        name: "Calls & texts",
        copy: "A business number for calls, voicemail, SMS and MMS, group texts and missed-call text back.",
        link: "/features/workspace-voice",
        icon: "sms",
        plans: [{ key: "voice" }],
      },
      {
        name: "Phone system & IVR",
        copy: "Route callers by menu, business hours, ring group or queue, with greetings and always-on call recording.",
        link: "/features/auto-attendant-and-ivr",
        icon: "call",
        plans: [{ key: "autoAttendant" }],
      },
      {
        name: "Post-call automation",
        copy: "AI call summaries, voicemail transcription and tasks created after each call.",
        link: "/features/post-call-automation",
        icon: "autoAwesome",
        plans: [{ key: "postCallAutomation" }],
      },
      {
        name: "Client portal",
        copy: "A private portal for each client, in your brand, with their invoices and the items waiting on them.",
        link: "/features/client-portal",
        icon: "portal",
        plans: [{ key: "clientPortal" }],
      },
      {
        name: "Branded emails & documents",
        copy: "Reminders, proposals, receipts and e-signature emails go out in your look, from your own mailbox.",
        link: "/features/branding",
        icon: "palette",
        plans: [{ key: "brandedDocuments" }],
      },
      {
        name: "Looks",
        copy: "Pick a whole look for your client portal in one tap: colors, fonts, corners and layout.",
        link: "/features/looks",
        icon: "autoAwesome",
        plans: [{ key: "branding" }],
      },
    ],
  },
  {
    id: "run-on-ai",
    eyebrow: "Run on AI",
    title: "AI that does the busywork and asks before it sends",
    intro: "AI AutoPilot reads and updates your real records, and anything that goes to a client waits for your approval.",
    scene: "ai-chat",
    cards: [
      {
        name: "AI AutoPilot chat",
        copy: "Ask about clients, invoices and projects, and let it draft, update and search across your workspace.",
        link: "/features/ai-chat",
        icon: "autoAwesome",
        plans: [{ key: "aiChat" }],
      },
      {
        name: "AutoPilot Today & briefing",
        copy: "A ranked list of what needs you today, Client Health flags and watches on money, projects and tasks.",
        link: "/features/autopilot",
        icon: "checkCircle",
        plans: [{ key: "smartSuggestions" }, { key: "aiDailyBrief", label: "Daily briefing" }],
      },
      {
        name: "Claude & ChatGPT connections",
        copy: "Connect Claude, ChatGPT and other AI apps over MCP, and approve what they want to send.",
        link: "/integrations/mcp",
        icon: "approvals",
        plans: [{ key: "aiConnections" }],
      },
      {
        name: "AI Import",
        copy: "Bring clients, services, invoices and proposals from a CSV or Excel export. AI maps the columns and you review every row.",
        link: "/integrations/csv-import",
        icon: "upload",
        plans: [{ key: "aiImport" }],
      },
      {
        name: "AI writing tools",
        copy: "Compose and rewrite messages, translate them for the client and summarize long threads.",
        link: "/tutorials/workspace-ai",
        icon: "edit",
        plans: [{ key: "aiCompose" }],
      },
      {
        name: "AI-drafted workflows",
        copy: "Describe an automation in plain words and get a workflow draft to review and test.",
        link: "/tutorials/workflows",
        icon: "bolt",
        plans: [{ key: "aiDraftedWorkflows" }],
      },
    ],
  },
  {
    id: "run-the-business",
    eyebrow: "Run the business",
    title: "Automations, numbers and the team behind them",
    intro: "Automate the follow-ups, see where the money is and give each teammate the access their role needs.",
    scene: "features/automation",
    cards: [
      {
        name: "Workflow Automations",
        copy: "Email and SMS steps, tasks, reminders and internal notifications, with timed waits, templates, test runs and logs.",
        link: "/features/automation",
        icon: "bolt",
        plans: [{ key: "automations" }, { key: "automations", label: "Text steps", full: true }],
      },
      {
        name: "Reports & analytics",
        copy: "Dashboards, attention signals and the client and team activity view.",
        link: "/features/analytics",
        icon: "chart",
        plans: [{ key: "analytics" }, { key: "arAging", label: "AR aging" }],
      },
      {
        name: "Expenses & receipts",
        copy: "Scan a receipt and get a prefilled expense, linked to the client or project it belongs to.",
        link: "/features/expenses",
        icon: "documentScanner",
        plans: [{ key: "expenses" }],
      },
      {
        name: "Products & inventory",
        copy: "A catalog of services and products for proposals and invoices, with stock levels and low-stock alerts.",
        link: "/features/products",
        icon: "inventory",
        plans: [{ key: "productsCatalog" }, { key: "productsCatalog", full: true, label: "Full catalog" }, { key: "inventory", label: "Inventory" }],
      },
      {
        name: "Accounting",
        copy: "A chart of accounts with AR aging and profitability, then journal entries, a P&L, balance sheet and year-end close.",
        link: "/features/accounting",
        icon: "balance",
        plans: [{ key: "accounting" }, { key: "financialStatements", label: "Full books" }],
      },
      {
        name: "Team roles & access",
        copy: "Roles and permissions for every teammate, and client-scoped access for contractors and account managers.",
        link: "/features/client-scoped-access",
        icon: "lock",
        plans: [{ key: "rolesPermissions" }, { key: "fullRbac", label: "Full role-based access" }],
      },
      {
        name: "Webhooks & API",
        copy: "Signed webhooks and the Builder API on every plan, for your own integrations and automation tools.",
        link: "/integrations/api-workflows",
        icon: "api",
        plans: [{ key: "webhooksApi" }, { key: "builderApi" }],
      },
      {
        name: "Workspace themes",
        copy: "A theme for each workspace, in light or dark mode.",
        link: "/features/themes",
        icon: "palette",
        plans: [{ key: "branding" }],
      },
      {
        name: "Support & onboarding",
        copy: "A help center, onboarding guidance and email support, with dedicated support for larger teams.",
        link: "/features/support",
        icon: "support",
        plans: [{ key: "emailSupport" }, { key: "dedicatedSupport", label: "Dedicated support" }],
      },
    ],
  },
  {
    id: "members",
    eyebrow: "Members & passes",
    title: "Sell memberships, check members in and run coaching programs",
    // Facts: release note 2026-11-02-sell-memberships-and-check-members-in and the Square +
    // membership LEAD's WEB-PAGES-FACTS-2026-11-04.md. Web app now, iPhone app with its next update;
    // payments through the business's own Stripe (Square is import-only); Stripe-sold recurring plans renew
    // automatically (packs and one-time types don't) (renewal job live since 11-02). Not live, never claim: kiosk, waivers, member
    // emails, pass designer. No pricing rows yet, so no cues. Programs (launched 11-02) come with Memberships;
    // only the Programs & Sessions report has a pricing row, cued on /features/programs.
    intro: "Build membership types from presets, sell them with a pay link through your own Stripe, check members in at the front desk and sell coaching programs in tiers. On the web app now; the iPhone app gets them with its next update.",
    cards: [
      { name: "Memberships & member passes", copy: "Unlimited, Minutes, Visit pack, Credits and Family presets, sold with a pay link. Recurring plans renew automatically, and each member's pass is in their client portal and Apple Wallet.", link: "/features/memberships", icon: "people", plans: [] },
      { name: "Front-desk check-ins", copy: "Check members in by camera, scanner or name search, with a clear reason when a pass can't be used.", link: "/features/check-ins", icon: "qrCode", plans: [] },
      { name: "Coaching programs", copy: "Sell coaching programs in tiers with a price, term and sessions each month, enrol clients with a coach and see who shows up.", link: "/features/programs", icon: "schedule", plans: [] },
    ],
  },
];

const planNames = pricing.plans.map((plan) => plan.name);
const rows = new Map(pricing.categories.flatMap((category) => category.rows.map((row) => [row.key, row.cells] as const)));
// Cells that don't make a feature available on that plan.
const missing = new Set(["no", "v:notIncluded", "v:comingSoon"]);
// Cells that make it available only in part.
const limited = new Set(["v:emailOnly", "v:starter", "v:watchesOnly", "v:upTo20", "v:basic", "v:builtInDefaults"]);

/** Index of the first plan (0 Cadet … 4 Enterprise) a row of the pricing matrix is on. */
export const planStart = (key: string, full = false): number => {
  const cells = rows.get(key);
  if (!cells) throw new Error(`features-hub: no pricing row "${key}"`);
  const first = cells.findIndex((cell) => !missing.has(cell) && !(full && limited.has(cell)));
  if (first < 0) throw new Error(`features-hub: pricing row "${key}" is on no plan`);
  return first;
};

/** "Every plan" or "<plan> and up" for one row of the pricing matrix. */
export const planCue = (key: string, full = false): string => {
  const first = planStart(key, full);
  return first === 0 ? "Every plan" : `${planNames[first]} and up`;
};

/** A card's cues: its own gate, then any labelled part that starts on a higher plan. `tier` is
    1 (Cadet) … 5 (Enterprise), for the app's plan palette. */
export const cardCues = (card: HubCard) =>
  card.plans
    .map((row) => ({ label: row.label, cue: planCue(row.key, row.full), tier: planStart(row.key, row.full) + 1 }))
    .filter((cue, index, all) => index === 0 || cue.cue !== all[0].cue);

/** The cues the hub uses, lowest plan first, for its legend. */
export const hubLegend = [...new Set(hubGroups.flatMap((group) => group.cards.flatMap((card) => card.plans.map((row) => planStart(row.key, row.full)))))]
  .sort((a, b) => a - b)
  .map((first) => ({ cue: first === 0 ? "Every plan" : `${planNames[first]} and up`, tier: first + 1 }));

export const hubCardCount = hubGroups.reduce((total, group) => total + group.cards.length, 0);
